import React, { Fragment, PropsWithChildren, useImperativeHandle, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import DialogueHandler from '@app/component/overlays/dialog/DialogueHandler';
import FormButton from '@app/component/form/button/FormButton';
import Utilities from '@util/Utilities';

type Props = {
	onCancel?: any,
	onConfirm?: any,
	confirmButtonName?: string,
	cancelButtonName?: string,
	title?: string
	isLoading?: boolean,
	noActions?: boolean
	size?: 'small' | 'medium' | 'large' | 'extra-large',
	isAutoClose?: boolean,
	ref: any,

}

const Dialogue: React.FunctionComponent<PropsWithChildren<Props>> = React.forwardRef(({
																																												children,
																																												title,
																																												onCancel,
																																												onConfirm,
																																												confirmButtonName = 'Yes',
																																												cancelButtonName = 'No',
																																												noActions = false,
																																												isLoading = false,
																																												isAutoClose = true,
																																												size = 'medium',
																																											}, ref: React.Ref<DialogueHandler>) => {

	const [isActive, setIsActive] = useState<boolean>(false);

	const open = () => {
		setIsActive(true);
	};
	const close = () => {
		setIsActive(false);
		if (!!onCancel) onCancel();
	};

	const confirm = () => {
		if (isAutoClose) setIsActive(false);
		if (!!onConfirm) onConfirm();
	};

	useImperativeHandle(ref, () => {
		return {
			open: open,
			close: close,
		};
	});

	const setSize = () => {
		switch (size) {
			case 'small' : {
				return 'max-w-sm';
			}
			case 'medium' : {
				return 'max-w-lg';
			}
			case 'large' : {
				return 'max-w-2xl';
			}
			case 'extra-large' : {
				return 'max-w-5xl';
			}
		}
	};

	return <Transition show={isActive} as={Fragment}>
		<Dialog as="div" className="relative z-10" onClose={!noActions ? close : () => {
		}}>
			<TransitionChild
				as={Fragment}
				enter="ease-out duration-300"
				enterFrom="opacity-0"
				enterTo="opacity-100"
				leave="ease-in duration-200"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
			>
				<div className="fixed inset-0 bg-white-50 bg-opacity-75 transition-opacity backdrop-blur-sm backdrop-filter" />
			</TransitionChild>

			<div className="fixed inset-0 z-10 overflow-y-auto">
				<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enterTo="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 translate-y-0 sm:scale-100"
						leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
					>
						<DialogPanel
							className={Utilities.classNames('relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full  sm:p-6', setSize())}>
							{!noActions && <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
								<button
									type="button"
									className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
									onClick={close}
								>
									<span className="sr-only">{cancelButtonName}</span>
									<XMarkIcon className="h-6 w-6" aria-hidden="true" />
								</button>
							</div>}
							<div>
								<div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
									{!!title && <DialogTitle as="h3"
																					 className="text-base font-semibold leading-6 text-gray-900">
										{title}
									</DialogTitle>}
									<div className="mt-4 text-gray-600">
										{children}
									</div>
									{!noActions && <div className="mt-6 sm:mt-4 sm:flex sm:justify-end">
										<FormButton state="white" extraClassName="mx-2"
																isLoading={isLoading}
																onClick={close}>{cancelButtonName}</FormButton>
										<FormButton state="primary" extraClassName="mx-2"
																isLoading={isLoading}
																onClick={confirm}>{confirmButtonName}</FormButton>
									</div>}
								</div>
							</div>
						</DialogPanel>
					</TransitionChild>
				</div>
			</div>
		</Dialog>
	</Transition>;
});

export default Dialogue;
